import { useState } from 'react';
import { toast } from 'sonner';
import { CRM_LINK_ROLES } from '@ip/shared-types';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';

/** `CRM_LINK_ROLES` is readonly; `hasCompanyRole` takes a plain array. */
const LINK_ROLES = [...CRM_LINK_ROLES];

interface Options {
  /** Called after a successful create/link AND after a concurrent 409 (the
   * account now has *some* contractor, from another click/tab — refetch). */
  onCreated: () => void;
}

/**
 * Shared "utwórz kontrahenta z konta CRM" action — used by the account
 * page's "Kontrahent" panel (`ContractorLinkCard`) and by the "Dokumenty"
 * tab's empty-state CTA, so the create call + toast/409 handling AND the
 * permission gate (`canCreate`) live in one place instead of being
 * duplicated between them.
 */
export function useCreateContractorFromAccount(nip: string, accountId: string, { onCreated }: Options) {
  const { createContractorFromAccount } = useCrmApi();
  const { isSuperAdmin, permissions } = useAuth();
  const [creating, setCreating] = useState(false);

  const canCreate = isSuperAdmin || hasCompanyRole(permissions, nip, LINK_ROLES);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const result = await createContractorFromAccount(nip, accountId);
      toast.success(
        result.created ? t('crm.accounts.contractor.created') : t('crm.accounts.contractor.linkedExisting'),
      );
      onCreated();
    } catch (error) {
      const status = (error as { status?: number })?.status;
      if (status === 409) {
        toast.error(t('crm.accounts.contractor.alreadyLinked'));
        // The account got linked concurrently (another click, another tab) —
        // refetch so the caller shows the contractor that is now actually there.
        onCreated();
      } else {
        toast.error(error instanceof Error ? error.message : t('errors.saveFailed'));
      }
    } finally {
      setCreating(false);
    }
  };

  return { canCreate, creating, handleCreate };
}
